import { useLang } from "../../i18n/LangContext";
import { useSite } from "../../context/SiteContext";
import { fileUrl } from "../../lib/api";
import { DynIcon } from "../../lib/icons";
import { SectionHead } from "./Brand";
import { CountUp, Particles } from "./Effects";

const img = (src, site) => fileUrl(src, site?.performance?.cdn_base_url);
const lazy = (site) => (site?.performance?.lazyload !== false ? "lazy" : "eager");

export const StatsBar = () => {
  const { statsbar: t } = useLang().t;
  return (
    <section className="relative z-10 mx-auto max-w-7xl px-4 pt-14 sm:px-6 lg:px-8" data-testid="statsbar-section">
      <div className="glass-card relative grid grid-cols-2 overflow-hidden md:grid-cols-4">
        <Particles testid="statsbar-particles" />
        {t.items.map((s, i) => (
          <div key={i} className="relative flex flex-col items-start gap-3 border-[#D4AF37]/15 p-4 sm:flex-row sm:items-center sm:gap-4 sm:p-7 [&:nth-child(odd)]:border-r md:[&:nth-child(odd)]:border-r-0 md:[&:not(:last-child)]:border-r" data-testid={`statsbar-item-${i}`}>
            <span className="fx-icon grid h-11 w-11 shrink-0 place-items-center rounded-full border border-[#D4AF37]/50 text-[#D4AF37]"><DynIcon name={s.icon} size={18} /></span>
            <div className="min-w-0">
              <p className="font-serif text-xl font-bold gold-text sm:text-3xl"><CountUp value={s.value} prefix={s.prefix} suffix={s.suffix} testid={`statsbar-value-${i}`} /></p>
              <p className="mt-0.5 text-[11px] uppercase tracking-widest text-zinc-500 sm:text-xs">{s.label}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const BetTypes = ({ onJoin }) => {
  const { bets: t } = useLang().t;
  const { site } = useSite();
  return (
    <section id="bet-types" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="bets-section">
      <SectionHead eyebrow={t.eyebrow} title={t.title} sub={t.sub} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {t.items.map((b, i) => (
          <button key={i} onClick={onJoin} data-testid={`bet-card-${i}`} className="glass-card fx-card group relative flex flex-col items-start justify-start overflow-hidden p-6 text-left">
            {b.image && <><img src={img(b.image, site)} alt={b.title} loading={lazy(site)} className="absolute inset-0 h-full w-full object-cover opacity-30 transition-opacity group-hover:opacity-45" /><div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" /></>}
            <div className="relative">
              <span className="fx-icon grid h-12 w-12 place-items-center rounded-xl border border-[#D4AF37]/50 bg-[#D4AF37]/5 text-[#D4AF37] transition-transform group-hover:-translate-y-1"><DynIcon name={b.icon} size={20} /></span>
              <h3 className="mt-5 font-serif text-lg font-semibold text-white">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{b.desc}</p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

export const Fixtures = ({ onJoin }) => {
  const { fixtures: t } = useLang().t;
  const { site } = useSite();
  return (
    <section id="fixtures" className="relative border-y border-[#D4AF37]/15 bg-[#0E0F14] py-24" data-testid="fixtures-section">
      <div className="streak" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow={t.eyebrow} title={t.title} sub={t.sub} />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {t.items.map((f, i) => (
            <div key={i} className="fx-card group relative flex min-h-[260px] flex-col overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-black" data-testid={`fixture-card-${i}`}>
              {f.image ? <img src={img(f.image, site)} alt={f.league} loading={lazy(site)} className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />
                : <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(212,175,55,0.25),transparent_60%)]" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
              <div className="relative flex flex-1 flex-col p-6">
                <span className="flex w-fit items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-black/50 px-3 py-1 text-[10px] uppercase tracking-widest text-[#F3D77A]"><DynIcon name="Calendar" size={11} />{f.date}</span>
                <h3 className="mt-auto pt-10 font-serif text-xl font-bold text-white">{f.league}</h3>
                <p className="mt-1 text-sm text-zinc-300">{f.teams}</p>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-zinc-500"><DynIcon name="MapPin" size={12} />{f.venue}</p>
                <button onClick={onJoin} data-testid={`fixture-cta-${i}`} className="gold-underline mt-4 w-fit text-xs font-semibold uppercase tracking-widest text-[#D4AF37]">{t.cta}</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
